import { productoCubreModo, PRODUCTOS_PLUS } from './productosPlus';

// Matemática pura del gating freemium de MiPAE — sin dependencias de
// supabase ni de nada que solo exista en el navegador. Se separó de
// `lib/plan.js` (deuda #2 del backlog de la fábrica) para que `api/claude.js`
// (Vercel Edge Function) pueda importar la MISMA fuente de verdad y validar
// la cuota en el servidor, en vez de duplicar a mano los números y reglas
// que ya vivían acá. `plan.js` re-exporta todo este módulo tal cual, así que
// ningún consumidor existente cambia.
//
// Tier de pago = "MiPAE+" (en código el valor de profiles.plan sigue siendo
// 'premium' por compatibilidad con la columna existente; "MiPAE+" es solo el
// nombre de cara al usuario).

// Cuota mensual del plan FREE (MiPAE+ = ilimitado).
//
// Es la cuota GENERAL. La capa de ejercicio (rol 'enfermero') consulta varias
// veces por turno, así que su tope de Revisa es más apretado (5/mes)
// para que el paywall caiga sobre el volumen real de uso. El resto comparte el
// tope general. La cuota efectiva se resuelve con `cuotaPara(profile)`.
// MiPAE extendido, Fase 1 (decisión del fundador 2026-09-14, plan en
// ops/planes/version-extendida.md): lo que se limita en el plan gratis es
// SOLO el uso intensivo de IA. `paes` = PAE Rápido y Detallado (ambos emiten
// la capa diagnosticos), `detallados` = PAE Detallado (su generación de
// diagnósticos, marcada con el módulo pae_detallado; NO la capa
// actividades_nic, que emiten los dos PAE una vez por diagnóstico, arreglo
// 2026-09-26), `revisas` = Revisa, `benitoDia` = llamadas de Benito
// POR DÍA (15 llamadas ≈ 12 mensajes: el loop promedia 1,19 vueltas). Un
// mensaje se evalúa ANTES de enviarse; Benito nunca corta a mitad de respuesta.
export const CUOTA_FREE = {
  paes: 5,
  detallados: 2,
  revisas: 10,
  benitoDia: 15,
  // PAE con tu pauta (2026-09-16): 1 al mes para probarlo; la extendida sin
  // tope. Se cuenta en la RPC mi_uso_pauta_mes (capa pae_pauta).
  pautas: 1,
};

// Límite de uso razonable de las membresías (Términos §6: "muy superior al
// de la cuenta gratuita, que no está pensado para alcanzarse con un uso
// normal"). Aprobados por Sebastián el 19-oct: 100/día y 300/mes. Los números se
// cambian SOLO aquí y las pantallas, /precios y el servidor los leen de aquí
// (fijado por test). Fundamento, con 60 días de producción hasta el 17-oct:
//   - Benito: 413 personas, 950 días con uso. La mitad de los días ≤5
//     consultas, el 99% ≤43 y el máximo 122. 100 al día no toca el uso
//     normal y acota el peor caso. El techo técnico de 150 sigue hasta el
//     cierre (TECHO_ACCESO_ABIERTO). Cuenta mensajes, no vueltas internas (mi_uso_benito_hoy).
//   - La evolución completa con IA vive aparte (CUOTA_EVOLUCION_SEMANA_PLUS).
// Lo cuenta el mismo contador del plan gratis; vale igual en las tres
// membresías (Full no suma dos veces, Términos §6) y en las pruebas gratuitas.
export const CUOTA_PLUS = {
  benitoDia: 100,
  benitoMes: 300,
};
// Mientras siga el acceso abierto (hasta el 24-oct inclusive) rige el techo
// técnico de siempre, que vale para todos los que usan la IA sin cuota. El
// límite de las membresías entra recién con el cierre: Sebastián pidió el
// 17-oct no reducir los límites del acceso abierto antes de esa fecha.
export const TECHO_ACCESO_ABIERTO = {
  benitoDia: 150,
};

// Overrides de cuota por rol (profiles.rol). Solo se declara lo que difiere del
// tope general; lo no declarado hereda CUOTA_FREE.
// Solo se declara lo que difiere: el resto HEREDA CUOTA_FREE (cuotaPara hace
// el merge), así que subir un tope general no exige tocar cada rol. Si
// octubre muestra que el tope de Benito muerde a enfermeros en turno, se
// sube acá con una línea (`enfermero: { ..., benitoDia: N }`).
export const CUOTA_POR_ROL = {
  estudiante: { paes: 1, detallados: 1, pautas: 1, benitoDia: 3 },
  // Capa ejercicio: el enfermero consulta varias veces por turno.
  enfermero: { revisas: 5 },
};

// Las cuentas existentes conservan sus condiciones mientras corre el aviso
// destacado en la app. Corte de altas: 11-oct; cambio: 25-oct, 00:00 Chile.
export const ESTUDIANTE_ALTAS_NUEVAS = '2026-10-11T03:00:00Z';
export const ESTUDIANTE_FIN_TRANSICION = '2026-10-25T03:00:00Z';
export function enTransicionEstudiante(profile, ahora = Date.now()) {
  const alta = Date.parse(profile?.created_at);
  return profile?.rol === 'estudiante' && Number.isFinite(alta)
    && alta < Date.parse(ESTUDIANTE_ALTAS_NUEVAS)
    && ahora < Date.parse(ESTUDIANTE_FIN_TRANSICION);
}

// Cuota efectiva del mes para un perfil dado. Lee profiles.rol.
export function cuotaPara(profile) {
  if (enTransicionEstudiante(profile)) return CUOTA_FREE;
  const rol = CUOTA_POR_ROL[profile?.rol];
  return rol ? { ...CUOTA_FREE, ...rol } : CUOTA_FREE;
}

// Features de IA con cuota y el campo de `uso` que las mide. `uso` viene de
// obtenerUsoMes (paes/detallados/revisas) + obtenerUsoBenitoHoy (benitoHoy).
// Catálogo cerrado: una feature fuera de esta tabla no tiene cuota.
const FEATURE_A_USO = {
  pae: { cuota: 'paes', uso: 'paes' },
  detallado: { cuota: 'detallados', uso: 'detallados' },
  revisa: { cuota: 'revisas', uso: 'revisas' },
  benito: { cuota: 'benitoDia', uso: 'benitoHoy' },
  pauta: { cuota: 'pautas', uso: 'pautas' },
};

// Suscripción pagada vigente. Antes solo miraba profile.plan, lo que dejaba el
// premium activo indefinidamente: el webhook de Flow escribe profiles.premium_until
// (ahora + 31/183 días) pero ningún gating lo aplicaba. Ahora se respeta la vigencia.
// Sin premium_until = grant sin vencimiento (cuentas internas/legacy): se mantiene.
export function esPremium(profile, ahora = Date.now()) {
  if (profile?.plan !== 'premium') return false;
  if (profile.premium_product && !productoCubreModo(profile.premium_product, profile.rol)) return false;
  if (!profile.premium_until) return true;
  return new Date(profile.premium_until).getTime() > ahora;
}

// Pruebas acordadas el 20-oct: Estudiante 3 días, Enfermero 7 días.
// Las nuevas pruebas requieren tarjeta; las anteriores conservan sus condiciones.
// El servidor fija producto y vencimiento
// al iniciarlas; cambiar el rol no extiende ni repite la prueba. Los accesos
// anteriores, sin esos campos, conservan sus siete días y su alcance.
export const DIAS_TRIAL = 7;
export const DIAS_TRIAL_POR_ROL = Object.freeze({ estudiante: 3, enfermero: 7 });

export function diasTrialPara(profile) {
  return DIAS_TRIAL_POR_ROL[profile?.rol] || 0;
}

export function finTrial(profile) {
  if (!profile?.trial_started_at) return NaN;
  return profile.trial_ends_at
    ? Date.parse(profile.trial_ends_at)
    : Date.parse(profile.trial_started_at) + DIAS_TRIAL * 86400000;
}

// ¿El trial está activo ahora? (independiente del plan pagado).
export function enTrial(profile, ahora = Date.now()) {
  const inicio = profile?.trial_started_at;
  if (!inicio) return false;
  return Date.parse(inicio) <= ahora && ahora < finTrial(profile);
}

export function pruebaCubreModo(profile, modo, ahora = Date.now()) {
  return enTrial(profile, ahora)
    && (!profile.trial_product || productoCubreModo(profile.trial_product, modo));
}

// MiPAE+ efectivo = suscripción pagada O trial activo. Es lo que abre la IA
// ilimitada; el gating se mide contra esto, no contra esPremium a secas.
// Lo pagado vale solo en la CASA (tres mundos, M3 de docs/plan-tres-mundos.md):
// el perfil con que se pinta otro mundo (perfilParaMundo en lib/mundos.js)
// lleva `mundoVisitado` y ahí rige el plan gratis de ese mundo. Pero SOLO con
// el acceso abierto cerrado: mientras siga abierto no se le quita a nadie lo
// que hoy tiene (§2 del plan), y eso incluye la exención de los topes de uso
// justo (PAE, Detallado y Benito), que rigen aunque el acceso esté abierto.
// El perfil real nunca trae `mundoVisitado`, así que la casa y el servidor no
// cambian.
export function esMipaePlus(profile, ahora = Date.now()) {
  if (profile?.trial_product && pruebaCubreModo(profile, profile.rol, ahora)) return true;
  if (profile?.premium_product) return esPremium(profile, ahora);
  if (profile?.mundoVisitado && !accesoPlusAbierto(ahora)) return false;
  return esPremium(profile, ahora) || pruebaCubreModo(profile, profile?.rol, ahora);
}

// Una prueba por cuenta, para Estudiante o Enfermero. TENS sigue gratuito.
// La RPC aplica la misma elegibilidad y bloquea reintentos concurrentes.
export function trialDisponible(profile) {
  return diasTrialPara(profile) > 0 && profile?.plan !== 'premium' && !profile?.trial_started_at;
}

// Días que le quedan al trial activo (0 si no hay trial / ya venció).
export function diasRestantesTrial(profile, ahora = Date.now()) {
  if (!enTrial(profile, ahora)) return 0;
  const fin = finTrial(profile);
  return Math.ceil((fin - ahora) / (24 * 60 * 60 * 1000));
}

// ¿Puede el usuario iniciar una acción de IA?
// feature ∈ { 'pae', 'detallado', 'revisa', 'benito' }.
// Plus (pagado o trial): sin cuota, salvo el techo de uso justo (hoy solo
// Benito): TECHO_ACCESO_ABIERTO mientras siga el acceso abierto y CUOTA_PLUS
// después del cierre. Free: compara contra la cuota por rol. Fail-open: sin
// datos de uso (RPC caída, campo ausente) no se bloquea a nadie; el riesgo es
// gastar de más un día, no dejar a alguien sin Benito por un fallo de red.
export function puedeUsarIA(feature, { profile, uso }) {
  const f = FEATURE_A_USO[feature];
  if (!f) return true;
  if (feature === 'benito') return periodoTopeBenito({ profile, uso }) === null;
  const usado = uso?.[f.uso];
  if (typeof usado !== 'number') return true; // fail-open
  if (esMipaePlus(profile)) {
    const techo = (accesoPlusAbierto() ? TECHO_ACCESO_ABIERTO : CUOTA_PLUS)[f.cuota];
    return techo === undefined || usado < techo;
  }
  if (profile?.rol === 'estudiante' && !enTransicionEstudiante(profile) && ['pae', 'detallado', 'pauta'].includes(feature)) {
    // Un solo cupo compartido: diagnósticos del Rápido/Detallado + pauta.
    // Si un contador ya agotó el cupo, no se permite pasar por faltar el otro.
    if (uso?.paes >= cuotaPara(profile).paes || uso?.pautas >= cuotaPara(profile).paes) return false;
    if (typeof uso?.paes !== 'number' || typeof uso?.pautas !== 'number') return true;
    return uso.paes + uso.pautas < cuotaPara(profile).paes;
  }
  return usado < cuotaPara(profile)[f.cuota];
}

// Benito: un contador por cuenta, compartido entre experiencias y por Full.
// La cuota mensual empieza con el cierre; un permiso antiguo sin producto
// conserva su techo técnico previo. Cada contador conocido se comprueba
// aunque el otro falle: la falta de un dato no invalida un tope ya alcanzado.
export function topeBenitoMes(profile, ahora = Date.now()) {
  if (accesoPlusAbierto(ahora) || !esMipaePlus(profile, ahora)) return null;
  if (!profile?.premium_product && esPremium(profile, ahora)) return null;
  return CUOTA_PLUS.benitoMes;
}

export const MENSAJE_TOPE_BENITO_MES = 'Este mes llegamos al límite de consultas a Benito. Se renuevan el día 1, a medianoche, hora de Chile. Tus conversaciones siguen guardadas.';

export function periodoTopeBenito({ profile, uso }) {
  const mensual = topeBenitoMes(profile);
  if (mensual !== null && typeof uso?.benitoMes === 'number' && uso.benitoMes >= mensual) return 'mes';
  const legado = !profile?.premium_product && esPremium(profile);
  const diario = esMipaePlus(profile)
    ? (accesoPlusAbierto() || legado ? TECHO_ACCESO_ABIERTO : CUOTA_PLUS).benitoDia
    : cuotaPara(profile).benitoDia;
  return typeof uso?.benitoHoy === 'number' && uso.benitoHoy >= diario ? 'dia' : null;
}

// Cuántos usos quedan. null si premium o sin datos. OJO anti-culpa: esto NO
// se muestra como contador "te quedan N" en pantalla (decisión 2026-09-14);
// sobrevive solo para lógica interna y hoy no tiene consumidor en pantalla.
export function usosRestantes(feature, { profile, uso }) {
  if (esMipaePlus(profile)) return null;
  const f = FEATURE_A_USO[feature];
  if (!f) return null;
  const usado = uso?.[f.uso];
  if (typeof usado !== 'number') return null;
  if (profile?.rol === 'estudiante' && !enTransicionEstudiante(profile) && ['pae', 'detallado', 'pauta'].includes(feature)) {
    if (typeof uso?.paes !== 'number' || typeof uso?.pautas !== 'number') return null;
    return Math.max(0, cuotaPara(profile).paes - uso.paes - uso.pautas);
  }
  return Math.max(0, cuotaPara(profile)[f.cuota] - usado);
}

// ── Lanzamiento abierto de MiPAE+ ────────────────────────────────────────────
// Decisión estratégica: mientras nadie ha pagado, MiPAE+ se libera para todos
// (bajar fricción, generar uso real, recoger feedback). Es un kill-switch único:
// ponlo en false para volver al modelo freemium sin tocar nada más — las
// funciones de arriba (esPremium/enTrial/esMipaePlus/puedeUsarIA) siguen
// calculando el estado real de pago/cuota intacto.
export const ACCESO_PLUS_ABIERTO = true;

// Fecha de cierre del lanzamiento abierto (medianoche Chile continental).
// Mientras sea null, el acceso queda abierto indefinidamente (gobierna el
// kill-switch de arriba). Al fijarla — vía REACT_APP_PLUS_FECHA_CIERRE
// ('YYYY-MM-DD') o editando el fallback — pasan dos cosas sin tocar nada más:
//   1. El aviso del Home muestra cuenta regresiva (AvisoLanzamientoPlus).
//   2. Llegada la fecha, el gating vuelve solo al freemium real (cuota/trial/
//      pago), sin deploy de emergencia ni cambio de flag a mano.
export const FECHA_CIERRE_PLUS = process.env.REACT_APP_PLUS_FECHA_CIERRE || null;

function tsCierrePlus() {
  if (!FECHA_CIERRE_PLUS) return null;
  // Medianoche REAL de Chile continental, con o sin horario de verano: se
  // prueban ambos offsets y gana el que Santiago rinde como las 00:00 de esa
  // fecha (barrido 2026-08-02: con -04:00 fijo, un cierre en meses de DST
  // corría una hora). Sin Intl con zonas, cae al comportamiento previo.
  const fallback = new Date(`${FECHA_CIERRE_PLUS}T00:00:00-04:00`).getTime();
  if (Number.isNaN(fallback)) return null;
  try {
    for (const off of ['-04:00', '-03:00']) {
      const ts = new Date(`${FECHA_CIERRE_PLUS}T00:00:00${off}`).getTime();
      const partes = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Santiago', hour12: false,
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit',
      }).formatToParts(ts);
      const p = (t) => partes.find((x) => x.type === t)?.value;
      const fecha = `${p('year')}-${p('month')}-${p('day')}`;
      if (fecha === FECHA_CIERRE_PLUS && (p('hour') === '00' || p('hour') === '24')) return ts;
    }
  } catch { /* Intl sin timeZone: fallback */ }
  return fallback;
}

// Lo que dice el servidor, SOLO en la app instalada (lib/configRemota.js).
// La web se reconstruye el 25 con las variables del paso a paso, pero la app
// de la tienda no: su código viene armado de antes. Para que el 25 no exija
// una segunda actualización ni quede programado a fecha fija (la activación
// la decide Sebastián ese día, plan-interfaz-por-experiencia §11.6), la app
// instalada sigue el mismo interruptor del servidor (`ACCESO_PLUS_ABIERTO` de
// Vercel, servido por api/app-config). null = no hay dato y manda lo de
// abajo. El servidor nunca lo fija: api/ usa este archivo sin tocarlo.
let accesoAbiertoRemoto = null;
export function fijarAccesoAbiertoRemoto(valor) {
  accesoAbiertoRemoto = typeof valor === 'boolean' ? valor : null;
}

// ¿Está el lanzamiento abierto AHORA? Única fuente de verdad del acceso
// liberado: kill-switch + fecha de cierre (si existe), o el servidor en la
// app instalada.
export function accesoPlusAbierto(ahora = Date.now()) {
  if (accesoAbiertoRemoto !== null) return accesoAbiertoRemoto;
  if (!ACCESO_PLUS_ABIERTO) return false;
  const cierre = tsCierrePlus();
  return cierre === null || ahora < cierre;
}

// Días completos que faltan para el cierre del acceso abierto.
// null = sin fecha fijada; 0 = cierra hoy / ya cerró.
export function diasParaCierrePlus(ahora = Date.now()) {
  const cierre = tsCierrePlus();
  if (cierre === null || !ACCESO_PLUS_ABIERTO) return null;
  return Math.max(0, Math.ceil((cierre - ahora) / (24 * 60 * 60 * 1000)));
}

// Compromiso de transparencia: días de aviso previo antes de que MiPAE+ pase a
// cobro. Se comunica en el aviso de lanzamiento (AvisoLanzamientoPlus).
export const DIAS_AVISO_PREVIO_COBRO = 14;

// MiPAE+ efectivo de cara al gating y la UI. Con el lanzamiento abierto, todos
// tienen acceso; si se cierra (flag o fecha), se respeta pago/trial (esMipaePlus).
export function tieneAccesoPlus(profile, ahora = Date.now()) {
  return ((profile?.rol !== 'estudiante' || enTransicionEstudiante(profile, ahora)) && accesoPlusAbierto(ahora)) || esMipaePlus(profile, ahora);
}

// Features cuyo tope rige AUNQUE el acceso abierto siga vigente (uso justo).
// Es la palanca del calendario de la extendida: los topes de IA entran con
// el aviso del 21-sep (14 días antes, como exigen los Términos) y el cierre
// del 5-oct solo apaga la venta oculta. Revisa NO entra: su cuota sigue
// colgando del acceso abierto como en junio. Vacunado por test.
const FEATURES_USO_JUSTO = new Set(['benito', 'pae', 'detallado']);
export function topeUsoJusto(feature) {
  return FEATURES_USO_JUSTO.has(feature);
}

// ¿Puede iniciar una acción de IA, considerando el lanzamiento abierto? Envuelve
// puedeUsarIA: con acceso abierto nunca topa con la cuota, SALVO las features
// de uso justo, que se consultan antes de mirar el acceso abierto.
export function puedeAccederIA(feature, ctx) {
  if ((ctx.profile?.rol === 'estudiante' && !enTransicionEstudiante(ctx.profile)) || topeUsoJusto(feature)) return puedeUsarIA(feature, ctx);
  return accesoPlusAbierto() || puedeUsarIA(feature, ctx);
}


// ── Evolución con IA de MiPAE en turno (Términos §6, desde el 25-oct-2026) ──
// La cuenta gratuita tiene UNA evolución completa con IA por semana (lunes a
// domingo, hora de Chile) y por cuenta, sin acumular. Una evolución es un
// paciente en un turno: ordenar, pegar, pulir y re-pulir ese mismo paciente
// cuentan una sola vez, y seguir con él nunca se bloquea (decisión del
// fundador del 16-oct). Lo cuenta la RPC mi_uso_evolucion_semana por
// metadata.evolucion_ref (el id local del registro, sin datos del paciente).
export const CUOTA_EVOLUCION_SEMANA = 1;
// Enfermero Plus y Full: evoluciones completas con IA por semana, con el
// mismo contador (paciente-turno, lunes a domingo). Aprobado por Sebastián
// el 19-oct. Fundamento: 15 personas y 36 semanas con uso en
// 60 días, la mediana 2 por semana, el 90% ≤10 y el máximo 13; 30 cubre una
// semana de cuatro turnos con siete pacientes cada uno. Cada evolución cuesta
// del orden de US$0,05 a US$0,08 de IA.
export const CUOTA_EVOLUCION_SEMANA_PLUS = 30;
export const CAPAS_EVOLUCION = Object.freeze(['evolucion_estructurar', 'evolucion_pulir']);
// Lo que se dice al llegar al tope, en UN solo lugar: lo responde el servidor
// (api/_shared/aiCalls.js) y lo muestra la ficha. Sin "te falta" ni compra,
// porque lo lee también la app instalada.
export const MENSAJE_CUOTA_EVOLUCION = 'Tu evolución con IA de esta semana ya la usaste con otro paciente. Se renueva el lunes. Lo que escribiste sigue aquí y puedes completar la evolución a mano.';

// ¿Esta cuenta usa la evolución con IA sin el tope semanal? Mientras siga el
// acceso abierto, todas (nada cambia antes del 25-oct). Después, solo quien
// tiene una membresía que cubre MiPAE Enfermero (Enfermero Plus o Full),
// un permiso antiguo sin producto (rige en todos los modos) o una prueba
// vigente. Estudiante Plus NO la cubre: no incorpora lo pagado de MiPAE
// Enfermero (oferta del 15-oct), así que la interna tiene la semanal gratis.
// Lo pagado depende de la membresía, no de la profesión: se mira el
// producto contra 'enfermero', nunca el rol. "Sin tope" aquí quiere decir
// sin el tope de la CUENTA GRATUITA (decide si se ve su aviso); el límite
// razonable de Enfermero Plus y Full lo da topeEvolucionSemana.
export function evolucionSinTope(profile, ahora = Date.now()) {
  // El permiso administrativo es independiente de la membresía. En el
  // servidor viene de profiles, nunca del cuerpo o los headers del cliente.
  if (profile?.is_admin === true) return true;
  if (accesoPlusAbierto(ahora)) return true;
  if (pruebaCubreModo(profile, 'enfermero', ahora)) return true;
  if (profile?.plan !== 'premium') return false;
  if (profile.premium_until && new Date(profile.premium_until).getTime() <= ahora) return false;
  if (!profile.premium_product) return true;
  return productoCubreModo(profile.premium_product, 'enfermero');
}

// Cuántas evoluciones con IA por semana tiene este perfil, o null si no
// tiene tope. Sin tope: el acceso abierto (hasta el 24-oct) y un permiso
// antiguo sin producto, que conserva sus condiciones. Enfermero Plus, Full y
// la prueba de 7 días: el límite razonable de la membresía (la prueba deja
// probar la membresía, ni más ni menos, igual que el techo de Benito). El
// resto: la cuenta gratuita.
export function topeEvolucionSemana(profile, ahora = Date.now()) {
  if (profile?.is_admin === true) return null;
  if (!evolucionSinTope(profile, ahora)) return CUOTA_EVOLUCION_SEMANA;
  if (accesoPlusAbierto(ahora)) return null;
  // El permiso antiguo va antes que la prueba: si una cuenta tiene los dos,
  // conserva sus condiciones.
  if (!profile?.premium_product && premiumVigente(profile, ahora)) return null;
  return CUOTA_EVOLUCION_SEMANA_PLUS;
}

// ¿Puede empezar o seguir esta evolución? `uso` = { usadas, incluida } de la
// RPC y `tope` el de topeEvolucionSemana. Sin dato se deja pasar
// (fail-open): el riesgo es regalar una evolución, nunca bloquear la
// documentación de un turno. Seguir con un paciente ya contado no se bloquea.
export function puedeUsarEvolucion(uso, tope = CUOTA_EVOLUCION_SEMANA) {
  if (tope === null) return true;
  if (!uso || typeof uso.usadas !== 'number') return true;
  if (uso.incluida) return true;
  return uso.usadas < tope;
}

// Lo que se dice al llegar al límite de la membresía (no el de la cuenta
// gratuita): sin compra, porque lo lee también la app instalada. Sin el
// número A PROPÓSITO: el cliente reconoce este texto por igualdad exacta
// (textoLimiteEvolucion), y si el número viviera aquí, cambiar el límite
// haría que una app instalada sin actualizar no lo reconociera y mostrara el
// texto de la cuenta gratuita. El número se informa en /precios y Planes.
export const MENSAJE_LIMITE_EVOLUCION_PLUS = 'Esta semana llegaste al límite de evoluciones con IA de tu membresía. Se renueva el lunes. Lo que escribiste sigue aquí y puedes completar la evolución a mano.';

// Qué texto se muestra ante un límite de evolución: el de la membresía si es
// el que mandó el servidor, y si no, el de la cuenta gratuita. Nunca un texto
// arbitrario que haya llegado con el código del límite.
export function textoLimiteEvolucion(texto) {
  return texto === MENSAJE_LIMITE_EVOLUCION_PLUS ? MENSAJE_LIMITE_EVOLUCION_PLUS : MENSAJE_CUOTA_EVOLUCION;
}

// Membresía pagada vigente, sin mirar qué cubre (eso lo decide cada regla).
function premiumVigente(profile, ahora) {
  if (profile?.plan !== 'premium') return false;
  return !profile.premium_until || new Date(profile.premium_until).getTime() > ahora;
}

// ── Memoria de ramos (definición del fundador del 16-oct) ───────────────────
// Pertenece a MiPAE Estudiante y la incluyen Estudiante Plus y Full. Depende
// de la membresía y de la experiencia en uso, no de la profesión: una
// enfermera con Full que pasa a MiPAE Estudiante tiene ahí sus ramos, y con
// Enfermero Plus no (no cubre MiPAE Estudiante). `mundoEnUso` es la
// experiencia que la persona está mirando (la de visita o su casa).
//   - Mientras siga el acceso abierto no cambia nada: la regla de siempre
//     (tieneAccesoPlus con el perfil real), sin restricciones nuevas.
//   - Un permiso antiguo sin producto o una prueba conservan sus condiciones
//     (valían en cualquier experiencia).
// Leer o escribir los ramos se decide con esto; los datos guardados nunca se
// borran por no tener acceso.
export function ramosDisponibles(profile, mundoEnUso, ahora = Date.now()) {
  if (accesoPlusAbierto(ahora)) return tieneAccesoPlus(profile, ahora);
  if (profile?.trial_product && pruebaCubreModo(profile, mundoEnUso, ahora)) return mundoEnUso === 'estudiante';
  if (profile?.trial_product && !profile?.premium_product) return esPremium(profile, ahora);
  if (!profile?.premium_product) return esMipaePlus(profile, ahora);
  return mundoEnUso === 'estudiante'
    && premiumVigente(profile, ahora)
    && productoCubreModo(profile.premium_product, 'estudiante');
}

// ── PDF de Benito (definición del fundador del 16-oct) ──────────────────────
// Lo incluyen Estudiante Plus, Enfermero Plus y Full. Esta es la red del
// SERVIDOR (api/pdf/generate.js), que no sabe en qué experiencia está la
// persona: basta una membresía vigente de cualquiera de los tres productos
// (la app ya lo pide con el perfil de la experiencia en uso), un permiso
// antiguo o una prueba. El acceso abierto lo decide quien llama.
export function pdfBenitoPorMembresia(profile, ahora = Date.now()) {
  // MiPAE TENS no necesita membresía en esta etapa, y eso incluye el PDF de
  // Benito (decisión de Sebastián, 17-oct-2026), igual que su Enciclopedia
  // completa (`tieneEnciclopediaCompleta`). Se mira el perfil REAL.
  if (profile?.rol === 'tens') return true;
  if (enTrial(profile, ahora)) return true;
  if (!premiumVigente(profile, ahora)) return false;
  return !profile.premium_product || Object.prototype.hasOwnProperty.call(PRODUCTOS_PLUS, profile.premium_product);
}

// ── La red de cuota del servidor con una membresía de producto ──────────────
// El servidor (api/claude.js) mide la cuota con el perfil REAL porque no sabe
// en qué experiencia está la persona. Con eso, alguien que compró Estudiante
// Plus y después cambió su rol a enfermero quedaba con la cuota gratis en el
// servidor mientras la app, en MiPAE Estudiante, le reconocía su membresía:
// un 403 injusto a quien pagó. La red del servidor nunca debe ser más
// estricta que la app con una membresía vigente, así que aquí la membresía de
// producto cuenta como pagada aunque no cubra su rol; la app sigue decidiendo
// por experiencia (más estricta). Sin producto, o vencida, el perfil no cambia.
export function perfilParaCuotaServidor(profile, ahora = Date.now()) {
  const productoId = premiumVigente(profile, ahora) ? profile.premium_product
    : enTrial(profile, ahora) ? profile.trial_product : null;
  const producto = PRODUCTOS_PLUS[productoId];
  if (!producto) return profile;
  if (productoCubreModo(productoId, profile.rol)) return profile;
  return { ...profile, rol: producto.modos[0] };
}


